/**
 * The client's master-managed accounts (MASTER_TRADES_VISIBILITY_PLAN.md): their positions are listed
 * beside the client's own on the Trade page, and their money is added to the balance. Kept live via the
 * user:<accountId> channel of each managed account. Only for the live account: demo has none.
 */
import { useEffect, useMemo } from 'react';
import { closePrice, D, pnl, usdPerPoint, type AccountSummary, type ManagedAccount, type ManagedBook, type Position, type UserEvent } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { ws } from '../lib/ws';
import { marketStore } from './market';
import { useActiveAccount } from './session';

interface ManagedState {
  liveId: string | null;
  accounts: ManagedAccount[];
  positions: Position[];
  closed: Position[];
}

const EMPTY: ManagedState = { liveId: null, accounts: [], positions: [], closed: [] };
export const managedStore = createStore<ManagedState>(EMPTY);

export async function reloadManaged() {
  const liveId = managedStore.get().liveId;
  if (!liveId) return;
  try {
    const b = await api.get<ManagedBook>('/api/copy/managed');
    if (managedStore.get().liveId === liveId) managedStore.set({ accounts: b.accounts, positions: b.positions, closed: b.closed });
  } catch {
    /* keep what we have; the next resync retries */
  }
}

function onManagedEvent(ev: UserEvent) {
  if (ev.type === 'balance') {
    managedStore.set((s) => ({ accounts: s.accounts.map((a) => (a.account.id === ev.account.id ? { ...a, account: ev.account } : a)) }));
  } else if (ev.type === 'position') {
    managedStore.set((s) => {
      const owner = s.accounts.find((a) => a.account.id === ev.position.accountId);
      if (!owner) return {};
      const p = { ...ev.position, liqPrice: null, managedBy: owner.copy.managedBy };
      const rest = s.positions.filter((x) => x.id !== p.id);
      if (p.status === 'open') return { positions: [p, ...rest] };
      return { positions: rest, closed: [p, ...s.closed.filter((x) => x.id !== p.id)] };
    });
  }
}

/** Mount once: follows the live account and keeps its managed accounts live. */
export function useManagedSync() {
  const acc = useActiveAccount();
  const liveId = acc?.kind === 'live' ? acc.id : null;
  const accountIds = useStore(managedStore, (s) => s.accounts.map((a) => a.account.id).join(','));
  useEffect(() => {
    managedStore.set({ ...EMPTY, liveId });
    if (!liveId) return;
    void reloadManaged();
    // Being assigned to (or taken off) a master comes with a notification on the live account.
    const unsub = ws.subscribe(`user:${liveId}`, (ev: UserEvent) => {
      if (ev.type === 'notification') void reloadManaged();
    });
    const t = window.setInterval(() => void reloadManaged(), 60_000);
    return () => {
      unsub();
      window.clearInterval(t);
    };
  }, [liveId]);
  useEffect(() => {
    if (!accountIds) return;
    const unsubs = accountIds.split(',').map((id) => ws.subscribe(`user:${id}`, onManagedEvent));
    return () => unsubs.forEach((u) => u());
  }, [accountIds]);
}

export const useManagedAccounts = () => useStore(managedStore, (s) => s.accounts);
export const useManagedPositions = () => useStore(managedStore, (s) => s.positions);
export const useManagedClosed = () => useStore(managedStore, (s) => s.closed);

export interface ManagedTotals {
  /** Cash in the managed accounts. */
  balance: D;
  /** balance + credit + open P&L, marked live from the tickers. */
  equity: D;
  upnl: D;
  /** P&L since the money was allocated (equity − invested). */
  pnl: D;
  /** Sum of the accounts' equity at 00:00 UTC today; null while any is unknown. */
  dayStart: D | null;
}

/** Totals of the managed accounts, re-marked live; null when the client has none. */
export function useManagedTotals(): ManagedTotals | null {
  const accounts = useManagedAccounts();
  const positions = useManagedPositions();
  const subs = useMemo(() => accounts.map((a) => ({ account: a.account, invested: a.copy.invested, dayStartEquity: a.dayStartEquity })), [accounts]);
  return useSubAccountTotals(subs, positions);
}

/** Totals of sub-accounts (managed accounts, Smart Portfolio investments) and their open positions, re-marked live from the tickers. Pass stable arrays. */
export function useSubAccountTotals(accounts: { account: AccountSummary; invested: string; dayStartEquity: string | null }[], positions: Position[]): ManagedTotals | null {
  const tickers = useStore(marketStore, (s) => s.tickers);
  const bySymbol = useStore(marketStore, (s) => s.bySymbol);
  return useMemo(() => {
    if (!accounts.length) return null;
    let upnl = D.ZERO;
    for (const p of positions) {
      const t = tickers[p.symbol];
      if (!t) continue;
      const exit = closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) });
      upnl = upnl.add(pnl(p.side, p.entryPrice, exit, p.qty, usdPerPoint(bySymbol[p.symbol]?.contractSize ?? '1', t.fx))).add(p.swap);
    }
    let balance = D.ZERO;
    let credit = D.ZERO;
    let invested = D.ZERO;
    let dayStart: D | null = D.ZERO;
    for (const a of accounts) {
      // != null: an API from before this field (front end deployed first) sends nothing.
      dayStart = dayStart && a.dayStartEquity != null ? dayStart.add(a.dayStartEquity) : null;
      balance = balance.add(a.account.balance);
      credit = credit.add(a.account.credit);
      invested = invested.add(a.invested);
    }
    const equity = balance.add(credit).add(upnl);
    return { balance, equity, upnl, pnl: equity.sub(invested), dayStart };
  }, [accounts, positions, tickers, bySymbol]);
}
